import { CSSObject } from "@emotion/react";
import { Button, LinkSVG, Toast } from "@newsela/angelou";
import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";

import { getAssignmentLink } from "@/assignments/utils/assignmentUtils";
import globalToasterRef from "@/base/globalToasterRef";
import { Assignment } from "@/types/models/Assignment.interface";
import getContentLabel from "@/utils/getContentLabel";
import uiTypes from "@/view/constants/uiTypes";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import contentTypes from "../constants/contentTypes";
import { rootCss, toastButtonCss } from "./CopyButton.style";

type uiTypesKeys = keyof typeof uiTypes;
type uiTypesValues = (typeof uiTypes)[uiTypesKeys];

interface CopyButtonInterface {
  buttonProps?: Record<string, unknown>;
  contentId: string;
  contentType?: keyof typeof contentTypes;
  iconOnly?: boolean;
  isStudent?: boolean;
  setIsAssignModalActive: (a: boolean) => void;
  triggerRef: React.Ref<HTMLButtonElement> | null;
  uiType: uiTypesValues;
  __cssFor?: CSSObject;
}

const CopyButton = ({
  buttonProps,
  contentId,
  contentType = contentTypes.ARTICLE,
  iconOnly = false,
  isStudent = true,
  setIsAssignModalActive,
  triggerRef,
  uiType,
  __cssFor = {},
}: CopyButtonInterface) => {
  const ButtonWithTrackEvent = withTrackEvent(
    Button,
  ) as React.ForwardRefExoticComponent<
    React.ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
  >;

  const { updatedAssignment } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment;
  };

  const currentAssignmentId = updatedAssignment?.content_id;
  const isDraftAssignment = updatedAssignment?.is_draft;

  const isAssignmentPublished =
    Boolean(currentAssignmentId) && !isDraftAssignment;

  const copyAssignmentLinkEventProperties = {
    objectType: objectTypes.assignment,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.share,
    streamProperties: {
      content_type: "Assignment",
      description: "Copy current assignment link",
      object_id: currentAssignmentId,
    },
  };

  const copyArticleLinkEventProperties = {
    actionName: actionNames.share,
    streamProperties: {
      content_type: contentType,
      description: "Copy current assignment link",
      object_id: contentId,
    },
  };

  const isGenerationGeniusVideo =
    contentType === contentTypes.GENERATION_GENIUS_VIDEO;

  const renderToastMessage = () => {
    if (
      uiType === uiTypes.LITE_USER_PREMIUM_CONTENT ||
      contentType === contentTypes.INTERACTIVE_VIDEO
    ) {
      return "Article link copied.";
    }

    if (isGenerationGeniusVideo && isStudent) {
      return "Video link copied";
    }

    const contentLabel = getContentLabel({
      content_type: contentType,
      label: "",
    });

    return (
      <>
        {contentLabel || "Article"} link copied. To share activities &
        instructions,&nbsp;
        <ButtonWithTrackEvent
          __cssFor={{ root: toastButtonCss }}
          flavor={Button.flavors.incognito}
          onClick={() => setIsAssignModalActive(true)}
          ref={triggerRef}
        >
          create an assignment.
        </ButtonWithTrackEvent>
      </>
    );
  };

  const handleCopyAction = async () => {
    const articleUrl = `${window.location.protocol}//${window.location.host}/view/${contentId}`;
    const shareUrl = isAssignmentPublished
      ? getAssignmentLink(currentAssignmentId, contentId)
      : articleUrl;

    await navigator?.clipboard?.writeText(shareUrl);
    globalToasterRef.current.displayToast({
      showIcon: true,
      statusColor: Toast.statusColor.success,
      children: isAssignmentPublished
        ? "Link to assignment copied"
        : renderToastMessage(),
    });
  };

  return (
    <ButtonWithTrackEvent
      __cssFor={{ root: [rootCss, __cssFor["root"]] }}
      ariaProps={{ "aria-label": "Copy link" }}
      dataProps={{
        "data-qa-selector": `copy-${isGenerationGeniusVideo ? "generation-genius-video" : "article"}-and-assignment-button`,
      }}
      flavor={Button.flavors.muted}
      icon={{
        SvgComponent: LinkSVG,
        alignment: Button.iconAlignments.left,
      }}
      onClick={() => {
        void handleCopyAction();
      }}
      {...buttonProps}
      {...(isAssignmentPublished
        ? copyAssignmentLinkEventProperties
        : copyArticleLinkEventProperties)}
    >
      {iconOnly ? null : "Copy link"}
    </ButtonWithTrackEvent>
  );
};

export default CopyButton;
